// Voyage as a media source (docs/contracts.md section 4). Voyage-specific code lives in this folder only, so it
// can move into a shared package later. Nothing here touches the Voyage project; it reads the same data
// Voyage reads.
//
// Where the list comes from:
//   default             the live playlist on Voyage's R2 bucket (R2_BASE/data/compositions.json), the same file
//                       voya.ge reads in production. https://voya.ge/data/compositions.json is not served
//                       (the Voyage build leaves it out; the URL returns the app's HTML).
//   ?playlist=<id>      R2_BASE/data/playlists/<id>.json (a playlist made in voya.ge/admin)
//   ?voyage=draft       dev server only: ../voyage/public/data/compositions.json (Voyage's draft list), served
//                       by the Vite middleware in vite.config.ts at /voyage-draft/compositions.json
// R2 answers every request (list, posters, audio, video, range requests too) with
// Access-Control-Allow-Origin: *, so fetch, <img crossOrigin>, and MediaElementSource work from any origin
// (dev and production) without a proxy.
//
// Media paths that start with /media/ are resolved against R2_BASE, as Voyage does in production.
//
// Play order (order.ts): shuffled or in playlist order per the playlist's `shuffle` setting in
// R2_BASE/data/playlists.json (Voyage admin), read as voya.ge reads it. ?voyage=draft shuffles, as Voyage's dev
// server does with its local drafts.

import type { MediaItem, MediaSource } from '../../audio/media';
import { shuffleSetting, type VoyagePlaylistsState } from './order';

export const R2_BASE = 'https://pub-6ca9cc23f20f458a9b88796543fa3ca0.r2.dev';

/** Voyage's own defaults for composition.config (voyage/src/core/composition.js). */
export const VOYAGE_CONFIG_DEFAULTS = {
  audioCrossfade: 1.5,
  brownNoiseDuration: 2.0,
};
/** Voyage fades the old composition out over this many seconds when it sequences (voyage/src/main.js). */
export const VOYAGE_FADE_OUT_S = 2.0;
/** Brown noise bridge level (voyage/src/audio/playback.js). */
export const VOYAGE_BRIDGE_GAIN = 0.12;

interface VoyageComposition {
  id: string;
  name?: string;
  video?: { main?: string; mainPoster?: string; overlay?: string; overlayPoster?: string };
  audio?: { primary?: string; ambient?: string };
  subtitles?: string[];
  config?: { audioCrossfade?: number; brownNoiseDuration?: number };
}

/**
 * Absolute URL of a Voyage media file, marked `?lr3d=1` when it is on Voyage's R2 bucket. R2 sends CORS headers
 * only to requests with an Origin header and sends no `Vary: Origin`, so a file voya.ge already showed (a plain
 * <img> / <video>, no Origin) sits in the browser cache without them, and the room's CORS fetch of the same URL
 * is then blocked (seen 2026-10-26 with a poster). The marker gives the room its own cache entry. R2 ignores it.
 */
export function mediaUrl(u: string | undefined): string | undefined {
  if (!u) return undefined;
  const url = new URL(u, R2_BASE);
  if (url.origin === new URL(R2_BASE).origin && !url.searchParams.has('lr3d')) url.searchParams.set('lr3d', '1');
  return url.href;
}

/** The ?playlist=<id> id when it is a valid Voyage playlist id, else null (Voyage then plays the live list). */
export function playlistId(q: URLSearchParams = new URLSearchParams(location.search)): string | null {
  const playlist = q.get('playlist');
  return playlist && /^[a-z0-9][a-z0-9-]{0,39}$/.test(playlist) ? playlist : null;
}

export function listUrl(q: URLSearchParams = new URLSearchParams(location.search)): string {
  const playlist = playlistId(q);
  if (playlist) return `${R2_BASE}/data/playlists/${playlist}.json?v=${Date.now()}`;
  if (q.get('voyage') === 'draft' && import.meta.env.DEV) return '/voyage-draft/compositions.json';
  return `${R2_BASE}/data/compositions.json?v=${Date.now()}`;
}

export function toMediaItem(c: VoyageComposition): MediaItem | null {
  const primary = mediaUrl(c.audio?.primary);
  if (!c.id || !primary) return null;
  const ambient = mediaUrl(c.audio?.ambient);
  const cfg = { ...VOYAGE_CONFIG_DEFAULTS, ...(c.config ?? {}) };
  return {
    id: c.id,
    title: c.name || c.id,
    audio: ambient ? [primary, ambient] : [primary],
    video: mediaUrl(c.video?.main),
    poster: mediaUrl(c.video?.mainPoster),
    subtitles: c.subtitles?.length ? c.subtitles : undefined,
    transition: {
      fade_in_s: cfg.audioCrossfade,
      fade_out_s: VOYAGE_FADE_OUT_S,
      bridge_gain: VOYAGE_BRIDGE_GAIN,
      bridge_out_s: cfg.brownNoiseDuration,
    },
  };
}

/**
 * Whether the list plays shuffled (true) or in its order (false): the playlist's `shuffle` in data/playlists.json,
 * the ?playlist one, else the live one. The file missing or unreadable = shuffle, as on voya.ge.
 */
export async function voyageShuffles(q: URLSearchParams = new URLSearchParams(location.search)): Promise<boolean> {
  const playlist = playlistId(q);
  if (!playlist && q.get('voyage') === 'draft' && import.meta.env.DEV) return true;
  try {
    const res = await fetch(`${R2_BASE}/data/playlists.json?v=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return true;
    return shuffleSetting((await res.json()) as VoyagePlaylistsState, playlist);
  } catch {
    return true;
  }
}

export class VoyageSource implements MediaSource {
  id = 'voyage';
  constructor(private url: string = listUrl()) {}

  /** See voyageShuffles(). */
  shuffles(): Promise<boolean> {
    return voyageShuffles();
  }

  async list(): Promise<MediaItem[]> {
    const res = await fetch(this.url, { cache: 'no-cache' });
    if (!res.ok) throw new Error(`voyage list ${this.url}: HTTP ${res.status}`);
    if (!(res.headers.get('content-type') || '').includes('json')) throw new Error(`voyage list ${this.url}: not JSON`);
    const data = (await res.json()) as unknown;
    if (!Array.isArray(data)) throw new Error('voyage list: expected an array of compositions');
    return data.map((c) => toMediaItem(c as VoyageComposition)).filter((i): i is MediaItem => i !== null);
  }
}
